'use client';

import {
  Box,
  Button,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';

import type { ZeroEtlPolicyEditState } from './ZeroEtlPolicyCreationFlow';
import { ZeroEtlDisclaimer } from './zeroEtlDisclaimer';
import { ColoredVariables } from './zeroEtlRouting';

const iamPrincipalConfig = getCloudFieldConfig(Provider.Aws, 'iamPrincipalArn');

const TAG_KEYS = [
  'eon:etl:data-access:database={database_name}',
  'eon:etl:data-access:table={table_name}',
];

export const ZeroEtlPolicyTableAccess = (
  props: StepProps<ZeroEtlPolicyEditState>
) => {
  const { allowedPrincipals, requirePrincipalTags } = props.wizardState;

  const addPrincipal = () =>
    props.setWizardState({
      ...props.wizardState,
      allowedPrincipals: [...allowedPrincipals, ''],
    });

  const removePrincipal = (index: number) => {
    const updated = [...allowedPrincipals];
    updated.splice(index, 1);
    props.setWizardState({ ...props.wizardState, allowedPrincipals: updated });
  };

  const updatePrincipal = (index: number, value: string) => {
    const updated = [...allowedPrincipals];
    updated[index] = value;
    props.setWizardState({ ...props.wizardState, allowedPrincipals: updated });
  };

  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={3}
      onBackClick={props.back}
      canGoNext={() =>
        allowedPrincipals.length > 0 &&
        allowedPrincipals.every(
          (p) => !!p && iamPrincipalConfig.validation.validateInput(p)
        )
      }
      nextButtonDisabledTooltip='Fix or remove invalid allowed principal entries'
      onNextClick={async () => {
        await props.finish();
      }}
      nextButtonText='Save'
      sx={{ padding: '0px' }}
      navigationComponent={
        <RenameEntityName
          label='Integration name'
          name={props.wizardState.name}
          onSubmit={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
    >
      <ZeroEtlDisclaimer />
      <Stack flexGrow={1} minHeight={0} overflow='auto' sx={{ padding: '4px' }}>
        <Stack
          gap='24px'
          className='mx-[min(100px,5vw)]'
          sx={{ marginTop: 'min(60px,3vw)', paddingBottom: '24px' }}
        >
          <Typography variant='body1' color='text.secondary'>
            Define which IAM roles can access your tables in the AWS Glue
            Catalog.
          </Typography>
          <AllowedPrincipals
            principals={allowedPrincipals}
            onAdd={addPrincipal}
            onRemove={removePrincipal}
            onUpdate={updatePrincipal}
          />
        </Stack>
        <Stack
          borderTop='1px solid var(--mui-palette-divider)'
          borderBottom='1px solid var(--mui-palette-divider)'
          paddingY='24px'
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            className='mx-[min(100px,5vw)]'
          >
            <Typography>Add tag-based restriction for allowed roles</Typography>
            <Stack direction='row' gap='12px' alignItems='center'>
              <Tooltip title='When enabled, allowed roles must include matching tags to access the tables.'>
                <i className=' material-symbols-help-outline-rounded w-[20px] h-[20px]' />
              </Tooltip>
              <Switch
                checked={requirePrincipalTags}
                onChange={(e) =>
                  props.setWizardState({
                    ...props.wizardState,
                    requirePrincipalTags: e.target.checked,
                  })
                }
              />
            </Stack>
          </Stack>
          {requirePrincipalTags && (
            <Stack
              gap='24px'
              className='mx-[min(100px,5vw)]'
              sx={{ paddingTop: '24px' }}
            >
              <Typography width='580px' variant='body1' color='text.secondary'>
                Only IAM roles with matching tags can access the generated
                databases and tables. Tag values are generated automatically
                from your table mapping.
              </Typography>
              <Stack gap='8px'>
                {TAG_KEYS.map((tag) => (
                  <Box
                    key={tag}
                    sx={{
                      border: '1px solid var(--mui-palette-divider)',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      width: 'fit-content',
                    }}
                  >
                    <ColoredVariables text={tag} variant='subtitle2' />
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
    </StepContainer>
  );
};

const AllowedPrincipals = ({
  principals,
  onAdd,
  onRemove,
  onUpdate,
}: {
  principals: string[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, value: string) => void;
}) => (
  <Stack gap='12px'>
    {principals.map((principal, index) => (
      <Stack key={index} direction='row' gap='8px' alignItems='flex-start'>
        <FormTextField
          size='small'
          title={null}
          placeholder={iamPrincipalConfig.placeholder}
          value={principal}
          validation={iamPrincipalConfig.validation}
          onChange={(e) => onUpdate(index, e.target.value)}
          sx={{ width: '500px' }}
        />
        {principals.length > 1 && (
          <IconButton
            size='small'
            onClick={() => onRemove(index)}
            aria-label='Remove principal'
            sx={{ marginTop: '4px' }}
          >
            <i className='material-symbols-close-rounded' />
          </IconButton>
        )}
      </Stack>
    ))}
    <Button
      size='small'
      variant='text'
      startIcon={<i className='material-symbols-add-rounded' />}
      onClick={onAdd}
      sx={{ alignSelf: 'flex-start' }}
    >
      Add Another
    </Button>
  </Stack>
);
